// TranscriptionController
// Handles video transcription (YouTube, TikTok, Instagram)

import { resourcesApi } from '../services/resources-api';
import { api, type Document } from '../services/api';

export interface TranscriptionCallbacks {
  getCurrentDoc: () => Document | null;
  setCurrentDoc: (doc: Document) => void;
  showNotification: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export class TranscriptionController {
  private callbacks: TranscriptionCallbacks;

  constructor(callbacks: TranscriptionCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Check if current document has a video URL
   */
  hasVideoUrl(): boolean {
    const doc = this.callbacks.getCurrentDoc();
    if (!doc) return false;
    const customFields = doc.meta.customFields as Record<string, string> | undefined;
    const url = customFields?.url || '';
    return this.isVideoUrl(url);
  }

  private isVideoUrl(url: string): boolean {
    return url.includes('youtube.com') || url.includes('youtu.be') ||
           url.includes('tiktok.com') || url.includes('instagram.com');
  }

  /**
   * Refresh transcribe button status
   */
  refreshButton(): void {
    const transcribeBtn = document.querySelector('.doc-info-transcribe-btn') as HTMLElement;
    const doc = this.callbacks.getCurrentDoc();
    if (transcribeBtn && doc) {
      this.updateButtonStatus(transcribeBtn);
    }
  }

  /**
   * Create transcribe button for info panel
   */
  createButton(): HTMLElement {
    const btn = document.createElement('button');
    btn.className = 'doc-info-transcribe-btn transcribe-btn';
    btn.innerHTML = '<span class="material-symbols-outlined">closed_caption</span>';

    this.updateButtonStatus(btn);

    btn.onclick = async (e) => {
      e.stopPropagation();
      await this.handleAction();
      this.updateButtonStatus(btn);
    };

    return btn;
  }

  /**
   * Update button color based on transcription status
   */
  updateButtonStatus(btn: HTMLElement): void {
    const doc = this.callbacks.getCurrentDoc();
    if (!doc) return;

    const customFields = doc.meta.customFields as Record<string, string> | undefined;
    const url = customFields?.url || '';
    const transcriptionStatus = customFields?.transcriptionStatus || '';

    btn.classList.remove('status-available', 'status-completed', 'status-processing', 'status-failed', 'status-disabled');

    const isVideo = this.isVideoUrl(url);

    if (!isVideo) {
      btn.classList.add('status-disabled');
      btn.title = 'No video URL';
    } else if (transcriptionStatus === 'completed') {
      btn.classList.add('status-completed');
      btn.title = 'Transcription completed';
    } else if (transcriptionStatus === 'processing') {
      btn.classList.add('status-processing');
      btn.title = 'Transcription in progress';
    } else if (transcriptionStatus === 'failed') {
      btn.classList.add('status-failed');
      btn.title = 'Transcription failed - click to retry';
    } else {
      btn.classList.add('status-available');
      btn.title = 'Click to transcribe';
    }
  }

  /**
   * Handle transcribe action
   */
  async handleAction(): Promise<void> {
    const doc = this.callbacks.getCurrentDoc();

    if (doc) {
      const customFields = doc.meta.customFields as Record<string, string> | undefined;
      const url = customFields?.url || '';

      if (this.isVideoUrl(url)) {
        this.callbacks.showNotification('Starting transcription...');
        try {
          await resourcesApi.ingest({
            sourceUrl: url,
            title: doc.meta.title,
            prefill: true
          });
          this.callbacks.showNotification('Transcription job started');

          const newCustomFields = {
            ...(doc.meta.customFields || {}),
            transcriptionStatus: 'processing'
          };
          const response = await api.updateDocument(doc.meta.id, { customFields: newCustomFields });
          if (response.success && response.data) {
            this.callbacks.setCurrentDoc(response.data);
          }

          this.refreshButton();
        } catch (error) {
          console.error('Transcription failed:', error);
          this.callbacks.showNotification('Transcription failed', 'error');
        }
        return;
      }
    }

    this.showModal();
  }

  /**
   * Show transcribe modal for manual URL entry
   */
  showModal(): void {
    document.getElementById('transcribe-modal')?.remove();

    const modal = document.createElement('div');
    modal.id = 'transcribe-modal';
    modal.innerHTML = `
      <div class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Transcribe YouTube Video</h3>
            <button class="modal-close">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="modal-body">
            <label class="modal-label">YouTube URL</label>
            <input type="text" id="transcribe-url-input" class="modal-input"
              placeholder="https://youtube.com/watch?v=..." autofocus />
            <p class="modal-hint">The video will be sent to Uniscribe for transcription and added to your Knowledge Base.</p>
          </div>
          <div class="modal-footer">
            <button class="modal-btn modal-btn-secondary" data-action="cancel">Cancel</button>
            <button class="modal-btn modal-btn-primary" data-action="submit">
              <span class="material-symbols-outlined">mic</span>
              Transcribe
            </button>
          </div>
        </div>
      </div>
      <style>
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .modal-content {
          background: var(--bg-main);
          border-radius: 12px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2);
        }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
        }
        .modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: var(--text-secondary);
        }
        .modal-close:hover { color: var(--text-main); }
        .modal-body { padding: 20px; }
        .modal-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 8px;
          color: var(--text-secondary);
        }
        .modal-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-main);
          color: var(--text-main);
          box-sizing: border-box;
        }
        .modal-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .modal-hint {
          margin: 12px 0 0;
          font-size: 12px;
          color: var(--text-muted);
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px 20px;
          border-top: 1px solid var(--border);
        }
        .modal-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border: none;
        }
        .modal-btn .material-symbols-outlined { font-size: 16px; }
        .modal-btn-secondary {
          background: var(--bg-hover);
          color: var(--text-main);
        }
        .modal-btn-secondary:hover { background: var(--border); }
        .modal-btn-primary {
          background: var(--primary);
          color: white;
        }
        .modal-btn-primary:hover { background: #1d4ed8; }
        .modal-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      </style>
    `;

    document.body.appendChild(modal);

    const input = document.getElementById('transcribe-url-input') as HTMLInputElement;
    const overlay = modal.querySelector('.modal-overlay');
    const closeBtn = modal.querySelector('.modal-close');
    const cancelBtn = modal.querySelector('[data-action="cancel"]');
    const submitBtn = modal.querySelector('[data-action="submit"]');

    const closeModal = () => modal.remove();

    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });

    submitBtn?.addEventListener('click', async () => {
      const url = input.value.trim();
      if (!url) {
        input.focus();
        return;
      }

      if (!url.includes('youtube.com') && !url.includes('youtu.be')) {
        alert('Please enter a valid YouTube URL');
        return;
      }

      (submitBtn as HTMLButtonElement).disabled = true;
      submitBtn.innerHTML = `
        <span class="material-symbols-outlined" style="animation: spin 1s linear infinite">progress_activity</span>
        Sending...
      `;

      try {
        const result = await resourcesApi.ingest({ sourceUrl: url, prefill: true });

        if (result.success && result.data) {
          closeModal();
          this.callbacks.showNotification('Video sent for transcription', 'success');
        } else {
          alert('Failed to send video for transcription');
          (submitBtn as HTMLButtonElement).disabled = false;
          submitBtn.innerHTML = `<span class="material-symbols-outlined">mic</span> Transcribe`;
        }
      } catch (error) {
        console.error('Transcription error:', error);
        alert('Failed to send video for transcription');
        (submitBtn as HTMLButtonElement).disabled = false;
        submitBtn.innerHTML = `<span class="material-symbols-outlined">mic</span> Transcribe`;
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submitBtn?.dispatchEvent(new Event('click'));
      if (e.key === 'Escape') closeModal();
    });

    setTimeout(() => input.focus(), 100);
  }
}
